/**
 * StatTile: label + big value + optional hint on a glass tile. Promoted from
 * the Dashboard (roles-users WU-15) — the Dashboard and Financial pages share
 * it. `tone="accent"` highlights the tile; `onClick` renders it as a button.
 * Styles: `styles/data.css` (stat tiles section).
 */
import type { JSX, ReactNode } from 'react';
import { cx } from './cx';

export interface StatTileProps {
  label: string;
  value: ReactNode;
  hint?: ReactNode;
  /** Visual tone; `'accent'` gives the accent border and value ink. */
  tone?: string;
  /** Makes the tile a button. */
  onClick?: () => void;
  'aria-label'?: string;
  'data-testid'?: string;
  className?: string;
}

export function StatTile({
  label,
  value,
  hint,
  tone,
  onClick,
  'aria-label': ariaLabel,
  'data-testid': testId,
  className,
}: StatTileProps): JSX.Element {
  const content = (
    <>
      <span className="bl-stat-tile__label">{label}</span>
      <span className="bl-stat-tile__value">{value}</span>
      {hint !== undefined && hint !== null && <span className="bl-stat-tile__hint">{hint}</span>}
    </>
  );
  const accent = tone === 'accent';
  return onClick === undefined ? (
    <div
      className={cx('bl-glass bl-stat-tile', accent && 'bl-stat-tile--accent', className)}
      data-tone={tone}
      data-testid={testId}
      aria-label={ariaLabel}
    >
      {content}
    </div>
  ) : (
    <button
      type="button"
      className={cx(
        'bl-glass bl-stat-tile bl-stat-tile--action',
        accent && 'bl-stat-tile--accent',
        className,
      )}
      data-tone={tone}
      data-testid={testId}
      onClick={onClick}
      aria-label={ariaLabel}
    >
      {content}
    </button>
  );
}
